import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Loader2, AlertTriangle, Trash2, RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('pt-PT'); } catch { return iso; }
};

export const SlugViolationsPage = () => {
  const [data, setData] = useState({ items: [], summary: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/slug-violations`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar violations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const clearAll = async () => {
    if (!window.confirm('Apagar TODOS os registos de violations? Útil para "limpar a tabela" depois de corrigir um bug.')) return;
    try {
      setClearing(true);
      const r = await axios.delete(`${API_URL}/admin/slug-violations`);
      toast.success(`Apagadas ${r.data.deleted} violations`);
      fetchData();
    } catch (err) {
      toast.error('Erro ao apagar');
    } finally {
      setClearing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="slug-violations-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-rose-500" />
              Slug Violations (Auditoria)
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Quando o frontend envia um valor em UPPERCASE para um campo que devia ser slug minúsculo (ex: "ENDESA" em vez de "endesa"), o backend normaliza automaticamente <strong>e</strong> regista aqui. Útil para detectar regressões no formulário antes que o utilizador as note.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="sv-refresh">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </Button>
            {data.total > 0 && (
              <Button onClick={clearAll} disabled={clearing} className="bg-rose-600 hover:bg-rose-700 gap-2" data-testid="sv-clear">
                {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Limpar tudo
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : data.total === 0 ? (
          <Card className="rounded-2xl border-emerald-100 bg-emerald-50/30">
            <CardContent className="text-center py-16">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3">
                <AlertTriangle className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-emerald-800 font-medium">Sem violations registadas</p>
              <p className="text-xs text-slate-500 mt-1">Tudo a funcionar correctamente — nenhum slug em UPPERCASE detectado.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary card */}
            <Card className="rounded-2xl border-amber-200 bg-amber-50/40">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-amber-700 mb-3">Resumo (top valores)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {data.summary.slice(0, 12).map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-amber-100">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">{s.field}</p>
                        <p className="font-mono text-sm text-rose-700 truncate" title={s.raw_value}>{s.raw_value}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">×{s.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Detail table */}
            <Card className="rounded-2xl border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead>Quando</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Coleção</TableHead>
                      <TableHead>Doc ID</TableHead>
                      <TableHead>Campo</TableHead>
                      <TableHead>Valor enviado</TableHead>
                      <TableHead>Normalizado para</TableHead>
                      <TableHead>User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((v, i) => (
                      <TableRow key={i} className="hover:bg-slate-50/50">
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(v.created_at)}</TableCell>
                        <TableCell className="text-xs text-slate-600">{v.source}</TableCell>
                        <TableCell className="text-xs">{v.collection}</TableCell>
                        <TableCell className="font-mono text-[11px] text-slate-500">{(v.doc_id || '').slice(0, 8)}…</TableCell>
                        <TableCell className="font-mono text-xs text-slate-700">{v.field}</TableCell>
                        <TableCell className="font-mono text-xs text-rose-700">{v.raw_value}</TableCell>
                        <TableCell className="font-mono text-xs text-emerald-700">{v.normalized_to}</TableCell>
                        <TableCell className="font-mono text-[11px] text-slate-400">{(v.user_id || '').slice(0, 8)}…</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SlugViolationsPage;
